"use client";

import { Check, Copy } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Text to copy (a prompt, a command, an address). The text is plain HTML, so it reads and selects without JavaScript;
 * the Copy button appears once the page has loaded.
 */
export function CopyBlock({ text, label }: { text: string; label: string }) {
  const t = useTranslations("agents");
  const [copied, setCopied] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error(error);
    }
  };

  // A multi-line block (a config file) keeps its lines and scrolls sideways; a one-liner wraps.
  const multiline = text.includes("\n");

  return (
    <div className="relative rounded-lg border border-input-border bg-input-bg">
      <pre
        aria-label={label}
        tabIndex={multiline ? 0 : undefined}
        className={cn(
          "p-3 pe-14 font-mono text-sm leading-relaxed text-foreground",
          multiline ? "overflow-x-auto whitespace-pre" : "whitespace-pre-wrap break-words"
        )}
      >
        {text}
      </pre>
      {ready && (
        <Button
          variant="outline"
          size="icon"
          onClick={handleCopy}
          className="absolute end-1.5 top-1.5"
          title={copied ? t("copied") : t("copy")}
        >
          {copied ? <Check className="size-4 text-primary" /> : <Copy className="size-4" />}
          <span className="sr-only">{copied ? t("copied") : t("copy")}</span>
        </Button>
      )}
    </div>
  );
}
